import { useEffect, useState } from "react";
import { PageHeader, Section } from "@/components/layout/Section";
import { Radio, Calendar, Bell, PlayCircle, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { LOGO_URL, APOSTLE_IMAGE } from "@/constants/branding";

import { EnableLiveAlertsButton } from "@/components/LiveNotifier";

// CLBC Official YouTube channel
const YT_CHANNEL_ID = "UC16NmvoXBV1rYb17-EnMylA";
const YT_HANDLE_URL = "https://www.youtube.com/@clbc_official";
const FEATURED_VIDEO_ID = "0cpM6YJs3q4";
const FEATURED_VIDEO_TITLE = "CLBC Prophetic All-Night Service 2026 — The Divine Edifice";

export default function WatchLive() {
  const [live, setLive] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/youtube/live-status");
        setLive(data);
      } catch {
        setLive({ is_live: false });
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const isLive = !!live?.is_live;

  return (
    <div data-testid="watchlive-page">
      <PageHeader
        kicker="Watch Live"
        title="You're never far from home."
        subtitle="Join our services in real time, wherever you are. When we're not live, catch the featured message below."
      />

      <Section>
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 card-soft overflow-hidden" data-testid="watchlive-main">
            {isLive ? (
              <div className="aspect-video bg-black relative">
                <iframe
                  src={`https://www.youtube.com/embed/${live.video_id}?autoplay=1`}
                  title="CLBC Live"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="watchlive-iframe"
                />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-widest">
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> Live now
                </div>
              </div>
            ) : (
              <div
                className="aspect-video relative overflow-hidden text-white"
                data-testid="watchlive-fallback"
              >
                <img src={APOSTLE_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
                <div className="absolute inset-0 bg-gradient-to-br from-royal-800 via-royal-800/95 to-royal-700" />
                <div className="relative h-full flex flex-col items-center justify-center text-center px-8 py-10">
                  <img src={LOGO_URL} alt="CLBC" className="w-16 h-16 rounded-full ring-2 ring-gold/60 shadow-lg" />
                  <div className="mt-6 inline-flex items-center gap-2 text-gold text-[11px] font-semibold tracking-[0.35em] uppercase">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold" />
                    Currently Offline
                  </div>
                  <h3 className="mt-4 font-serif text-3xl sm:text-4xl leading-tight max-w-xl">
                    We go live every <span className="gold-text">Sunday at 12&nbsp;noon</span>
                  </h3>
                  <p className="mt-3 text-white/70 text-sm max-w-md">
                    Also join us Thursdays at 7&nbsp;PM for our Midweek Service. Subscribe on YouTube to get notified the moment we go live.
                  </p>
                  <div className="mt-8 flex flex-wrap gap-3 justify-center">
                    <EnableLiveAlertsButton className="btn-primary text-sm py-2.5" />
                    <Link
                      to="/sermons"
                      className="btn-secondary !border-white !text-white hover:!bg-white hover:!text-royal text-sm py-2.5"
                    >
                      <PlayCircle className="w-4 h-4" /> Watch Past Sermons
                    </Link>
                  </div>
                </div>
              </div>
            )}
            <div className="p-6 flex flex-wrap gap-4 items-center justify-between">
              <div>
                <div className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-widest ${isLive ? "text-red-500" : "text-slate-400"}`}>
                  <span className={`w-2 h-2 rounded-full ${isLive ? "bg-red-500 animate-pulse" : "bg-slate-300"}`} />
                  {isLive ? "Live now" : "Live when we gather"}
                </div>
                <h3 className="font-serif text-2xl text-royal mt-2">
                  {isLive && live?.title ? live.title : "Christ Love Breed · Live Broadcast"}
                </h3>
              </div>
              <Link to="/sermons" className="btn-secondary text-sm py-2.5">Recent Sermons</Link>
            </div>
          </div>

          <div className="space-y-6">
            <div className="card-soft p-6">
              <div className="w-11 h-11 rounded-full bg-gold/15 text-gold-500 flex items-center justify-center">
                <Calendar className="w-5 h-5" />
              </div>
              <h4 className="font-serif text-xl text-royal mt-4">Broadcast Schedule</h4>
              <ul className="mt-4 space-y-3 text-sm text-slate-600">
                <li className="flex justify-between"><span>Sunday Worship</span><span className="text-royal font-semibold">12:00 noon</span></li>
                <li className="flex justify-between"><span>Midweek (Thursday)</span><span className="text-royal font-semibold">7:00 PM</span></li>
              </ul>
            </div>

            <div className="card-soft p-6">
              <div className="w-11 h-11 rounded-full bg-royal/10 text-royal flex items-center justify-center">
                <Bell className="w-5 h-5" />
              </div>
              <h4 className="font-serif text-xl text-royal mt-4">Never Miss a Service</h4>
              <p className="mt-2 text-slate-600 text-sm">
                Subscribe to our YouTube channel and turn on notifications to be alerted when we go live.
              </p>
              <a
                href={YT_HANDLE_URL + "?sub_confirmation=1"}
                target="_blank"
                rel="noreferrer"
                className="btn-primary text-sm mt-5 w-full"
                data-testid="watchlive-subscribe-btn"
              >
                <Radio className="w-4 h-4" /> Subscribe on YouTube
              </a>
            </div>

            <div className="rounded-2xl p-6 bg-gradient-to-br from-royal to-royal-800 text-white">
              <div className="flex items-center gap-2 text-gold text-[10px] tracking-[0.3em] uppercase font-semibold">
                <Clock className="w-3.5 h-3.5" /> Ghana Time · GMT
              </div>
              <p className="mt-3 text-white/80 text-sm leading-relaxed">
                All service times are in <span className="text-gold font-semibold">Accra local time</span>. Wherever you are in the world, we look forward to worshipping with you.
              </p>
            </div>
          </div>
        </div>

        {/* Featured Video */}
        <div className="mt-16" data-testid="watchlive-featured">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <div className="kicker mb-3">Featured Message</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-royal-800 tracking-tight leading-tight max-w-2xl">
                {FEATURED_VIDEO_TITLE}
              </h2>
            </div>
            <Link to="/sermons" className="btn-ghost">All Sermons</Link>
          </div>
          <div className="card-soft overflow-hidden">
            <div className="aspect-video bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${FEATURED_VIDEO_ID}`}
                title={FEATURED_VIDEO_TITLE}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="watchlive-featured-iframe"
              />
            </div>
            <div className="p-6 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gold text-royal-800 flex items-center justify-center">
                <PlayCircle className="w-5 h-5" />
              </div>
              <div className="text-sm text-slate-600">
                A prophetic all-night service exploring <span className="text-royal font-semibold">The Divine Edifice</span> — how God builds His people into a holy temple by the Spirit.
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
